/**
 * OnboardingRoute - Wrapper d'authentification pour la route /onboarding
 *
 * ✅ Pattern TanStack Query pour vérification auth (comme ProtectedSuperAdminRoute)
 * ✅ Design System Landing (teal gradient)
 * ✅ FIX: Utilise data de la query pour éviter race condition avec Zustand
 */

import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getAccessToken, setAccessToken } from '@/services/tokenManager';
import { useAuthStore } from '@/stores/authStore';
import { authAPI, sessionsAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { OnboardingWizard } from './OnboardingWizard';

// Floating particles - même design que OnboardingWizard
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-lime-400/30 rounded-full animate-float"
        style={{
          left: `${10 + i * 12}%`,
          top: `${15 + (i % 4) * 20}%`,
          animationDelay: `${i * 0.6}s`,
          animationDuration: `${4 + i * 0.5}s`
        }}
      />
    ))}
  </div>
);

export const OnboardingRoute: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, setUser, setNeedsCompany } = useAuthStore();

  // ✅ TanStack Query pour vérification/refresh de session
  // FIX: Ne pas utiliser `enabled` pour éviter race condition
  // La query vérifie toujours l'auth et utilise le cache si disponible
  const { data: authData, isLoading, isError, isFetched } = useQuery({
    queryKey: ['auth', 'verify-onboarding'],
    queryFn: async () => {
      // Si déjà authentifié avec user, retourner directement
      if (isAuthenticated && user) {
        logger.log('✅ OnboardingRoute: Déjà authentifié, skip refresh');
        return { authenticated: true, user };
      }

      let token = getAccessToken();

      // Si pas de token en mémoire, tenter un refresh via cookie HttpOnly
      if (!token) {
        logger.log('🔐 OnboardingRoute: Pas de token, tentative de refresh...');
        try {
          const refreshResult = await sessionsAPI.refresh();
          if (refreshResult?.accessToken) {
            const expiresIn = refreshResult.expiresIn || 900;
            setAccessToken(refreshResult.accessToken, expiresIn);
            token = refreshResult.accessToken;
            logger.log('✅ OnboardingRoute: Token restauré via cookie');
          } else {
            logger.log('❌ OnboardingRoute: Refresh retourné sans token');
            return { authenticated: false, user: null };
          }
        } catch (err) {
          logger.log('❌ OnboardingRoute: Refresh échoué:', err);
          return { authenticated: false, user: null };
        }
      }

      // Charger les données utilisateur
      if (token) {
        logger.log('🔐 OnboardingRoute: Chargement des données utilisateur...');
        try {
          const userData = await authAPI.me();
          if (userData) {
            // Mettre à jour le store Zustand
            setUser(userData);

            const hasCompany = !!(
              userData.companyId &&
              userData.companyId !== null &&
              userData.companyId !== undefined
            );
            setNeedsCompany(!hasCompany);

            logger.log('✅ OnboardingRoute: User chargé:', userData.email, 'hasCompany:', hasCompany);
            return { authenticated: true, user: userData };
          }
        } catch (err) {
          logger.log('❌ OnboardingRoute: Erreur chargement user:', err);
        }
      }

      return { authenticated: false, user: null };
    },
    retry: false,
    staleTime: 30 * 1000, // 30 secondes
    // FIX: Pas de condition `enabled` - on vérifie toujours, le cache gère le reste
  });

  // ✅ Loading pendant la vérification (style Landing)
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
        <div className="text-center relative z-10">
          <Loader2 className="w-12 h-12 text-lime-400 animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">{t('onboarding.route.verifyingSession')}</p>
        </div>
      </div>
    );
  }

  // ✅ Utiliser les données de la query pour la décision (pas Zustand!)
  // Cela évite la race condition où Zustand n'est pas encore mis à jour
  const isAuthOk = authData?.authenticated === true || (isFetched && isAuthenticated);

  // ✅ Si erreur ou pas authentifié → redirection vers login
  if (isError || !isAuthOk) {
    logger.log('🔄 OnboardingRoute: Not authenticated, redirecting to login', {
      isError,
      authData,
      isAuthenticated,
      isFetched,
    });
    return <Navigate to="/login" replace />;
  }

  // ✅ Auth OK → rendre OnboardingWizard
  logger.log('✅ OnboardingRoute: Auth OK, rendering OnboardingWizard');
  return <OnboardingWizard />;
};

export default OnboardingRoute;
